import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FieldSet, Label, Select } from 'ext/components/ui';
import { userPropertiesShape } from 'ext/entities/user-properties';
import {
  getContinue,
  getClose,
  getNoOp,
  Shape as ActionsShape,
} from 'lib/actions';
import {
  GoToUrlOption,
  TriggerFlowOption,
  DismissFlowOption,
  CustomStepOption,
  TrackEventOption,
  UserPropertiesOption,
  SubmitFormOption,
  RequestReviewOption,
  RequestPushOption,
} from './options';
import {
  ACTION_TYPES,
  ACTION_TYPES_LIST,
  getActionTypeConfig,
} from './helpers';

function ActionsConfig({
  actions,
  context,
  hasSurveyBlock,
  userProperties,
  isExperienceFirstStep,
  onActionsChange,
}) {
  const { label, filterActions = [] } = context || {};

  const [actionConfig, setActionConfig] = useState(
    getActionTypeConfig(actions) || {}
  );

  const [actionType, setActionType] = useState(
    actionConfig.type || ACTION_TYPES.NO_OP
  );

  useEffect(() => {
    if (actions) {
      const actionTypeConfig = getActionTypeConfig(actions);
      setActionType(actionTypeConfig.type || ACTION_TYPES.NO_OP);
      setActionConfig(actionTypeConfig);
    }
  }, [actions]);

  const actionOptions = useMemo(
    () =>
      ACTION_TYPES_LIST.reduce(
        (acc, action) =>
          filterActions.includes(action.value)
            ? acc
            : [
                ...acc,
                {
                  label: action.label,
                  value: action.value,
                },
              ],
        []
      ),
    [filterActions]
  );

  const handleActionType = ({ value }) => {
    setActionType(value);
    setActionConfig({});

    let newAction;

    if (value === ACTION_TYPES.NEXT_STEP) {
      newAction = getContinue();
    }

    if (value === ACTION_TYPES.PREVIOUS_STEP) {
      newAction = getContinue({ offset: -1 });
    }

    if (value === ACTION_TYPES.DISMISS_FLOW) {
      newAction = getClose({ markComplete: true });
    }

    if (value === ACTION_TYPES.NO_OP) {
      newAction = getNoOp();
    }

    if (newAction) {
      onActionsChange({ actions: [newAction] });
    }
  };

  return (
    <>
      <FieldSet>
        <Label htmlFor={`${label}-action`}>{`${label} action`}</Label>
        <Select
          id={`${label}-action`}
          value={actionOptions.filter(({ value }) => actionType === value)}
          options={actionOptions.filter(
            ({ value }) =>
              !isExperienceFirstStep || value !== ACTION_TYPES.PREVIOUS_STEP
          )}
          onChange={handleActionType}
          placeholder="Select"
          portal
        />
      </FieldSet>

      {actionType === ACTION_TYPES.URL && (
        <GoToUrlOption
          config={actionConfig?.config}
          onChange={onActionsChange}
        />
      )}

      {actionType === ACTION_TYPES.TRIGGER_FLOW && (
        <TriggerFlowOption
          config={actionConfig?.config}
          onChange={onActionsChange}
        />
      )}

      {actionType === ACTION_TYPES.DISMISS_FLOW && (
        <DismissFlowOption
          actions={actions}
          config={actionConfig?.config}
          onChange={onActionsChange}
        />
      )}

      {actionType === ACTION_TYPES.CUSTOM_STEP && (
        <CustomStepOption
          config={actionConfig?.config}
          onChange={onActionsChange}
        />
      )}

      <TrackEventOption
        config={actionConfig?.config}
        onChange={onActionsChange}
      />

      <UserPropertiesOption
        userPropertiesList={userProperties}
        config={actionConfig?.config}
        onChange={onActionsChange}
      />

      {hasSurveyBlock && (
        <SubmitFormOption actions={actions} onChange={onActionsChange} />
      )}

      {actionType === ACTION_TYPES.DISMISS_FLOW && (
        <>
          <RequestReviewOption actions={actions} onChange={onActionsChange} />
          <RequestPushOption actions={actions} onChange={onActionsChange} />
        </>
      )}
    </>
  );
}

ActionsConfig.propTypes = {
  actions: PropTypes.arrayOf(ActionsShape),
  context: PropTypes.shape({
    label: PropTypes.string,
    description: PropTypes.string,
    filterActions: PropTypes.arrayOf(PropTypes.string),
  }),
  hasSurveyBlock: PropTypes.bool,
  userProperties: userPropertiesShape,
  isExperienceFirstStep: PropTypes.bool,
  onActionsChange: PropTypes.func,
};

export default ActionsConfig;
